'use client'

import Link from 'next/link'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { track } from '@/lib/analytics/track'

/* -------------------------------------------------------------------------- */
/*  TrackClick — generic wrapper for CTA buttons, comparison rows, etc.       */
/* -------------------------------------------------------------------------- */

export function TrackClick({
  event,
  properties,
  metaStandardEvent,
  children,
}: {
  event: string
  properties: Record<string, unknown>
  /**
   * Meta Pixel standard event to send instead of a custom one — set this on a
   * page's primary conversion CTA (e.g. `"Lead"`).
   *
   * Without it the click reaches Meta as `trackCustom`, which Meta will not
   * optimize or attribute against until someone hand-builds a custom conversion
   * in Events Manager. PostHog and the Google tag keep the granular `event`
   * name either way.
   */
  metaStandardEvent?: string
  children: React.ReactNode
}) {
  return (
    <span
      onClick={() => track(event, properties, { metaStandardEvent })}
      className="contents"
    >
      {children}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/*  TrackedFaqAccordion — FAQ section with expand tracking                     */
/* -------------------------------------------------------------------------- */

interface FaqItem {
  question: string
  answer: string
}

export function TrackedFaqAccordion({
  slug,
  faqs,
  defaultOpen = false,
}: {
  slug: string
  faqs: FaqItem[]
  defaultOpen?: boolean
}) {
  return (
    <Accordion
      type="multiple"
      defaultValue={defaultOpen ? faqs.map((_, i) => `faq-${i}`) : undefined}
      className="w-full"
    >
      {faqs.map((faq, index) => (
        <AccordionItem key={index} value={`faq-${index}`}>
          <AccordionTrigger
            className="text-left text-base font-medium"
            onClick={() =>
              track('tool_faq_expand', {
                slug,
                question: faq.question.slice(0, 80),
              })
            }
          >
            {faq.question}
          </AccordionTrigger>
          <AccordionContent className="text-sm leading-7 text-muted-foreground">
            {faq.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

/* -------------------------------------------------------------------------- */
/*  TrackedRelatedTool — related tool card with click tracking                 */
/* -------------------------------------------------------------------------- */

export function TrackedRelatedTool({
  slug,
  clickedSlug,
  position,
  href,
  title,
  description,
}: {
  slug: string
  clickedSlug: string
  position: number
  href: string
  title: string
  description: string
}) {
  return (
    <Link
      href={href}
      onClick={() =>
        track('tool_related_click', {
          slug,
          clicked_slug: clickedSlug,
          position,
        })
      }
      className="group rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/30 hover:bg-muted/50"
    >
      <h3 className="text-base font-semibold text-foreground group-hover:text-primary">
        {title}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {description}
      </p>
    </Link>
  )
}
